import { Alert } from '@mui/material';
import { PropsWithChildren, useDeferredValue, useEffect, useRef } from 'react';
import { BootAwareLoader, HideBootLoader } from './components/Loader/BootLoader';
import { isDebugMode } from './debug/debugUser';
import { useInitApp } from './hooks/useInitApp';
import { FormulaFieldsFlagSync } from './services/state/AdminPanel/FormulaFieldsFlagSync';
import { endAppLoadSpan } from './sentry-init';
import { LoadingStatus } from './types';
import { primeAfterBoot } from './util/landingPrefetch';
import { warmPages } from './util/lazyPage';
import { sectionPreloads } from './AppRoutes2';
import { shellPreloads } from './NavWrapper';

export function AppLoader({ children }: PropsWithChildren) {
  const loadStatus = useInitApp();
  // Deferred, so the switch to the shell renders as a transition: while the shell waits for its data the
  // boot loader stays up, and the shell commits as soon as the data lands. As a plain update the switch
  // committed the outer Suspense fallback, and React then held the shell for its 300 ms fallback throttle.
  const overallStatus = useDeferredValue(loadStatus.overallStatus);
  const spanEndedRef = useRef(false);

  useEffect(() => {
    if (overallStatus === LoadingStatus.success && !spanEndedRef.current) {
      spanEndedRef.current = true;
      endAppLoadSpan();
      // The first screen is on its way; fetch what the user can open next while they read it.
      // Not in debug mode, where the e2e suite runs; background downloads are noise a test does not need.
      if (!isDebugMode()) {
        warmPages({ shell: shellPreloads(), first: sectionPreloads() });
        primeAfterBoot();
      }
    }
  }, [overallStatus]);

  if (overallStatus == LoadingStatus.error) {
    return (
      <>
        <HideBootLoader />
        <Alert severity='error'>Application Error occurred</Alert>
      </>
    );
  }

  if (overallStatus !== LoadingStatus.success) {
    return <BootAwareLoader height='90vh' />;
  }

  return (
    <>
      <FormulaFieldsFlagSync />
      {children}
    </>
  );
}
